import { XmlParseError, amount, child, children, joinNonEmpty, text } from "./dom";
import { XmlDocumentType, XmlInvoice, XmlLine, XmlParty, XmlPayment, XmlVatRecap } from "./types";

const ROOT_NAMESPACES: Record<string, string> = {
    Invoice: "urn:oasis:names:specification:ubl:schema:xsd:Invoice-2",
    CreditNote: "urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2",
};

const INVOICE_TYPE_CODES: Record<string, XmlDocumentType> = {
    "381": "credit_note",
    "383": "debit_note",
    "386": "advance_invoice",
};

const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]{8,30}$/;

function round(value: number): number {
    return Math.round(value * 100) / 100;
}

function documentType(root: Element): XmlDocumentType {
    if (root.localName === "CreditNote") return "credit_note";

    return INVOICE_TYPE_CODES[text(root, "InvoiceTypeCode") ?? ""] ?? "invoice";
}

function vatNumber(party: Element | null): string | null {
    const vat = children(party, "PartyTaxScheme").find((s) => text(s, "TaxScheme", "ID") === "VAT");

    return text(vat ?? null, "CompanyID");
}

function parseParty(element: Element | null): XmlParty {
    const party = child(element, "Party");
    const address = child(party, "PostalAddress");

    return {
        name:
            text(party, "PartyName", "Name") ?? text(party, "PartyLegalEntity", "RegistrationName"),
        companyNumber:
            text(party, "PartyLegalEntity", "CompanyID") ??
            text(party, "PartyIdentification", "ID"),
        vatNumber: vatNumber(party),
        street: joinNonEmpty(
            [text(address, "StreetName"), text(address, "AdditionalStreetName")],
            ", ",
        ),
        city: text(address, "CityName"),
        postalZone: text(address, "PostalZone"),
        country: text(address, "Country", "IdentificationCode"),
    };
}

function parsePayment(means: Element): XmlPayment {
    const account = child(means, "PayeeFinancialAccount");
    const accountId = text(account, "ID");
    const isIban = !!accountId && IBAN_PATTERN.test(accountId.replace(/\s/g, ""));

    return {
        meansCode: text(means, "PaymentMeansCode"),
        bankAccount: isIban ? null : accountId,
        iban: isIban ? accountId : null,
        bic: text(account, "FinancialInstitutionBranch", "ID"),
    };
}

function unitPrice(line: Element): number | null {
    const price = amount(line, "Price", "PriceAmount");
    if (price === null) return null;

    return price / (amount(line, "Price", "BaseQuantity") || 1);
}

function description(item: Element | null): string | null {
    const name = text(item, "Name");
    const detail = text(item, "Description");

    return name && detail && name !== detail ? `${name} – ${detail}` : (name ?? detail);
}

function parseLine(line: Element): XmlLine {
    const quantityElement = child(line, "InvoicedQuantity") ?? child(line, "CreditedQuantity");
    const item = child(line, "Item");
    const totalExclVat = amount(line, "LineExtensionAmount");
    const vatRate = amount(item, "ClassifiedTaxCategory", "Percent");

    return {
        id: text(line, "ID"),
        description: description(item),
        quantity: amount(quantityElement),
        unit: quantityElement?.getAttribute("unitCode") || null,
        unitPrice: unitPrice(line),
        vatRate,
        totalExclVat,
        totalInclVat:
            totalExclVat !== null && vatRate !== null
                ? round(totalExclVat * (1 + vatRate / 100))
                : null,
    };
}

function parseVatRecap(subtotal: Element): XmlVatRecap {
    const base = amount(subtotal, "TaxableAmount");
    const vat = amount(subtotal, "TaxAmount");

    return {
        rate: amount(subtotal, "TaxCategory", "Percent"),
        base,
        vat,
        total: base !== null && vat !== null ? round(base + vat) : null,
    };
}

function documentTaxTotal(root: Element, currency: string | null): Element | null {
    const totals = children(root, "TaxTotal");
    const inCurrency = totals.find(
        (t) => child(t, "TaxAmount")?.getAttribute("currencyID") === currency,
    );

    return (
        inCurrency ?? totals.find((t) => children(t, "TaxSubtotal").length > 0) ?? totals[0] ?? null
    );
}

function rootElement(doc: Document): Element {
    const root = doc.documentElement;

    if (ROOT_NAMESPACES[root.localName] !== root.namespaceURI) {
        throw new XmlParseError(`Not a UBL document: ${root.namespaceURI ?? ""} ${root.localName}`);
    }
    if (!child(root, "LegalMonetaryTotal")) throw new XmlParseError("Missing LegalMonetaryTotal");

    return root;
}

export default function parseUbl(doc: Document): XmlInvoice {
    const root = rootElement(doc);
    const currency = text(root, "DocumentCurrencyCode");
    const paymentMeans = children(root, "PaymentMeans");
    const taxTotal = documentTaxTotal(root, currency);
    const monetaryTotal = child(root, "LegalMonetaryTotal");
    const lineName = root.localName === "CreditNote" ? "CreditNoteLine" : "InvoiceLine";

    return {
        documentType: documentType(root),
        number: text(root, "ID"),
        variableSymbol: paymentMeans.map((m) => text(m, "PaymentID")).find(Boolean) ?? null,
        issueDate: text(root, "IssueDate"),
        taxPointDate: text(root, "TaxPointDate"),
        dueDate:
            text(root, "DueDate") ??
            paymentMeans.map((m) => text(m, "PaymentDueDate")).find(Boolean) ??
            null,
        currency,
        supplier: parseParty(child(root, "AccountingSupplierParty")),
        customer: parseParty(child(root, "AccountingCustomerParty")),
        payments: paymentMeans.map(parsePayment),
        lines: children(root, lineName).map(parseLine),
        vatRecap: children(taxTotal, "TaxSubtotal").map(parseVatRecap),
        totals: {
            lineTotal: amount(monetaryTotal, "LineExtensionAmount"),
            allowances: amount(monetaryTotal, "AllowanceTotalAmount"),
            charges: amount(monetaryTotal, "ChargeTotalAmount"),
            exclVat: amount(monetaryTotal, "TaxExclusiveAmount"),
            vat: amount(taxTotal, "TaxAmount"),
            inclVat: amount(monetaryTotal, "TaxInclusiveAmount"),
            rounding: amount(monetaryTotal, "PayableRoundingAmount"),
            paidDeposits: amount(monetaryTotal, "PrepaidAmount"),
            payable: amount(monetaryTotal, "PayableAmount"),
        },
    };
}
